import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";

const HIDDEN_KEY = ["hidden"] as const;

type ToggleVars = { secid: string; nextOn: boolean };

// Скрытые/недоступные бумаги — отдельное множество secid'ов в кеше TanStack
// Query, по той же схеме что и useFavorites: оптимистичный toggle с откатом
// при ошибке сети.
//
// Важно: TanStack Query v5 запускает onMutate ДО mutationFn, поэтому решение
// «add vs remove» принимается в toggle() по pre-state и приходит явным флагом
// в variables. Если читать .has(secid) внутри mutationFn — там уже
// оптимистично-обновлённый кэш, и API-вызов будет инвертирован.
export function useHidden() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: HIDDEN_KEY,
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET("/api/hidden", { signal });
      if (error || !data) throw new Error(String(error ?? "no data"));
      return new Set(data.map((h) => h.secid));
    },
    staleTime: 5 * 60_000,
  });

  const mutation = useMutation({
    mutationFn: async ({ secid, nextOn }: ToggleVars) => {
      const result = nextOn
        ? await api.POST("/api/hidden/{secid}", {
            params: { path: { secid } },
            body: { reason: null },
          })
        : await api.DELETE("/api/hidden/{secid}", {
            params: { path: { secid } },
          });
      if (result.error) throw new Error(String(result.error));
    },
    onMutate: async ({ secid, nextOn }: ToggleVars) => {
      await qc.cancelQueries({ queryKey: HIDDEN_KEY });
      const prev = qc.getQueryData<Set<string>>(HIDDEN_KEY);
      qc.setQueryData<Set<string>>(HIDDEN_KEY, (old) => {
        const next = new Set(old);
        if (nextOn) next.add(secid);
        else next.delete(secid);
        return next;
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(HIDDEN_KEY, ctx.prev);
    },
  });

  return {
    hidden: query.data ?? new Set<string>(),
    toggle: (secid: string) => {
      const cur = qc.getQueryData<Set<string>>(HIDDEN_KEY) ?? new Set<string>();
      mutation.mutate({ secid, nextOn: !cur.has(secid) });
    },
  };
}
